import { useEffect, useMemo, useState } from "react";

import { speciesName } from "@/data/speciesNames";
import { speciesFormArtCandidates } from "@/lib/formSpriteCandidates";
import { officialArtworkUrl, homeArtworkUrl } from "@/lib/pokemonArtwork";

/**
 * Full-colour, shiny-aware comparison sprite. The comparison tiles show a
 * KNOWN species (something you or your friend caught / want to trade), so we
 * render the real official-artwork (HOME render fallback) instead of the dex
 * silhouette. Shiny rows use the /shiny artwork variant.
 *
 * Lifted out of Compare.tsx so the Trade Hub panes draw their sprites from the
 * same fallback chain — a lead tile and a trade-list row must never disagree
 * about which artwork a form resolves to.
 */
export function CompareSprite({
  id,
  shiny,
  size = 30,
  formId,
}: {
  id: number;
  shiny: boolean;
  size?: number;
  formId?: string | null;
}) {
  // A form lead shows the FORM's art (white Galarian Darumaka, not the red
  // Kanto one); everything else keeps the base-species artwork chain.
  const formChain = useMemo(
    () => speciesFormArtCandidates(id, formId, { shiny }) ?? [],
    [id, formId, shiny],
  );
  const [src, setSrc] = useState(() => formChain[0] ?? officialArtworkUrl(id, { shiny }));
  const chain = useMemo(
    () => [...formChain, officialArtworkUrl(id, { shiny }), homeArtworkUrl(id, { shiny })],
    [formChain, id, shiny],
  );
  useEffect(() => setSrc(chain[0]), [chain]);
  return (
    <img
      src={src}
      alt={speciesName(id)}
      width={size}
      height={size}
      loading="lazy"
      decoding="async"
      data-testid={`compare-sprite-${id}${shiny ? "-shiny" : ""}`}
      className="object-contain"
      style={{ width: size, height: size }}
      onError={() => {
        // Walk the chain: form art (if any) → official artwork → HOME render.
        const at = chain.indexOf(src);
        const next = chain[at + 1];
        if (next && next !== src) setSrc(next);
      }}
    />
  );
}
